import { ExternalLink } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { useUpdateServer } from '@/hooks/use-update-server';
import { cn } from '@/lib/utils';
import type { ServerListItem } from '@/types/fleet';

import { LastSeen } from './last-seen';
import { ProtocolChip } from './protocol-chip';
import { ProviderBadge } from './provider-badge';
import { RegionBadge } from './region-badge';
import { StatusBadge } from './status-badge';

interface ServerDrawerProps {
  server: ServerListItem | null;
  onClose: () => void;
}

function Row({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-4 text-sm">
      <span className="shrink-0 font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="text-right">{children}</span>
    </div>
  );
}

export function ServerDrawer({ server, onClose }: ServerDrawerProps): JSX.Element {
  const updateServer = useUpdateServer();
  const isStaging = server?.environment === 'staging';

  function flipEnvironment(): void {
    if (!server) return;
    const target = isStaging ? 'production' : 'staging';
    updateServer.mutate(
      { name: server.name, patch: { environment: target } },
      {
        onSuccess: () => toast.success(`${server.name} moved to ${target}`),
        onError: (e) => toast.error(e.message ?? 'Failed to change environment'),
      },
    );
  }

  // Region editing. Provision-time geoIP frequently mislabels a server's
  // location (e.g. a London IP resolving to DE), and there was no way to
  // correct it without re-provisioning. These map to the backend
  // UpdateServerDto region_* fields.
  const [editingRegion, setEditingRegion] = useState(false);
  const [continentDraft, setContinentDraft] = useState('');
  const [countryDraft, setCountryDraft] = useState('');
  const [cityDraft, setCityDraft] = useState('');

  // Proxy DNS hostname editing. Used by the browser extension's HTTPS_CONNECT
  // path — POST /proxy/session returns `dnsHostname || publicIp`, and the
  // extension TLS-validates against this name, so it must match the
  // proxy-ingress cert (else the connection fails to validate).
  const [editingDns, setEditingDns] = useState(false);
  const [dnsDraft, setDnsDraft] = useState('');

  // Reset edit state whenever a different server is opened in the drawer.
  useEffect(() => {
    setEditingRegion(false);
    setEditingDns(false);
  }, [server?.name]);

  function startEditRegion(): void {
    if (!server) return;
    setContinentDraft(server.region.continent ?? '');
    setCountryDraft(server.region.country ?? '');
    setCityDraft(server.region.city ?? '');
    setEditingRegion(true);
  }

  function saveRegion(): void {
    if (!server) return;
    updateServer.mutate(
      {
        name: server.name,
        patch: {
          region_continent: continentDraft.trim() || undefined,
          region_country: countryDraft.trim().toUpperCase() || undefined,
          region_city: cityDraft.trim() || undefined,
        },
      },
      {
        onSuccess: () => {
          toast.success(`${server.name} region updated`);
          setEditingRegion(false);
        },
        onError: (e) => toast.error(e.message ?? 'Failed to update region'),
      },
    );
  }

  function startEditDns(): void {
    if (!server) return;
    setDnsDraft(server.dns_hostname ?? '');
    setEditingDns(true);
  }

  function saveDns(): void {
    if (!server) return;
    // Empty clears it — the backend falls back to publicIp when unset.
    updateServer.mutate(
      { name: server.name, patch: { dns_hostname: dnsDraft.trim() } },
      {
        onSuccess: () => {
          toast.success(`${server.name} proxy hostname updated`);
          setEditingDns(false);
        },
        onError: (e) => toast.error(e.message ?? 'Failed to update proxy hostname'),
      },
    );
  }

  return (
    <Drawer open={server !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DrawerContent className="sm:max-w-sm">
        {server && (
          <>
            <DrawerHeader className="flex items-start justify-between">
              <div className="space-y-1 min-w-0">
                <DrawerTitle className="truncate">{server.name}</DrawerTitle>
                <div className="flex items-center gap-2">
                  <StatusBadge status={server.status} />
                  {isStaging && (
                    <span className="inline-flex items-center rounded border border-status-degraded/30 bg-status-degraded/10 px-1.5 py-0.5 font-mono text-[11px] uppercase tracking-wide text-status-degraded">
                      staging
                    </span>
                  )}
                </div>
              </div>
              <Link
                to={`/fleet/${encodeURIComponent(server.name)}`}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground shrink-0 ml-4"
                onClick={onClose}
              >
                Open <ExternalLink className="h-3.5 w-3.5" aria-hidden />
              </Link>
            </DrawerHeader>

            <div className="space-y-3 px-6 pb-6">
              <Row label="Public IP"><span className="font-mono">{server.public_ip}</span></Row>
              <Row label="Provider"><ProviderBadge provider={server.provider} /></Row>
              <Row label="Region">
                {editingRegion ? (
                  <div className="flex flex-col items-end gap-2">
                    <Input
                      value={continentDraft}
                      onChange={(e) => setContinentDraft(e.target.value)}
                      placeholder="Continent (e.g. Europe)"
                      className="h-7 w-44 text-xs"
                    />
                    <Input
                      value={countryDraft}
                      onChange={(e) => setCountryDraft(e.target.value.toUpperCase())}
                      maxLength={2}
                      placeholder="Country ISO-2 (e.g. GB)"
                      className="h-7 w-44 text-xs font-mono uppercase"
                    />
                    <Input
                      value={cityDraft}
                      onChange={(e) => setCityDraft(e.target.value)}
                      placeholder="City (e.g. London)"
                      className="h-7 w-44 text-xs"
                    />
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => setEditingRegion(false)}>
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={updateServer.isPending}
                        onClick={saveRegion}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-end gap-2">
                    <RegionBadge region={server.region} />
                    <Button size="sm" variant="outline" onClick={startEditRegion}>
                      Edit
                    </Button>
                  </div>
                )}
              </Row>
              <Row label="Proxy hostname">
                {editingDns ? (
                  <div className="flex flex-col items-end gap-2">
                    <Input
                      value={dnsDraft}
                      onChange={(e) => setDnsDraft(e.target.value)}
                      placeholder="proxy-xx.getpocketvpn.com"
                      className="h-7 w-56 text-xs font-mono"
                    />
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => setEditingDns(false)}>
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={updateServer.isPending}
                        onClick={saveDns}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-end gap-2">
                    <span className="font-mono text-xs text-muted-foreground">
                      {server.dns_hostname || '—'}
                    </span>
                    <Button size="sm" variant="outline" onClick={startEditDns}>
                      Edit
                    </Button>
                  </div>
                )}
              </Row>
              <Row label="Role"><span className="capitalize">{server.role.replace('_', ' ')}</span></Row>
              <Row label="Environment">
                <div className="flex items-center justify-end gap-2">
                  <span
                    className={cn(
                      'rounded border px-1.5 py-0.5 font-mono text-[11px] uppercase tracking-wide',
                      isStaging
                        ? 'border-status-degraded/30 bg-status-degraded/10 text-status-degraded'
                        : 'border-status-active/30 bg-status-active/10 text-status-active',
                    )}
                  >
                    {server.environment ?? 'production'}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={updateServer.isPending}
                    onClick={flipEnvironment}
                  >
                    {isStaging ? 'Promote to production' : 'Move to staging'}
                  </Button>
                </div>
              </Row>
              <Row label="Last seen"><LastSeen timestamp={server.last_seen_at} /></Row>
              {server.protocols.length > 0 && (
                <Row label="Protocols">
                  <div className="flex flex-wrap justify-end gap-1">
                    {server.protocols.map((p) => (
                      <ProtocolChip key={p.protocol} protocol={p.protocol} enabled={p.enabled} />
                    ))}
                  </div>
                </Row>
              )}
            </div>

            <DrawerClose className="sr-only" />
          </>
        )}
      </DrawerContent>
    </Drawer>
  );
}
